import React, { createContext, useContext, useEffect, useState } from 'react';
import { Box, Typography, Button, CircularProgress } from '@mui/material';
import { auth, signIn, signOut } from '../firebase';
import { onAuthStateChanged } from 'firebase/auth';

// Admin allow-list (mirrors the Firestore security rules for /orders, /catalogue).
const ALLOWED_EMAILS = ['sales@emmersongray.co.uk', 'john@emmersongray.co.uk'];

const AuthContext = createContext({ user: null });

/** Access the signed-in admin user anywhere below <AuthGate>. */
export const useAuth = () => useContext(AuthContext);

/**
 * Shared authentication guard for the whole admin app.
 * Renders children only for an approved, signed-in admin; otherwise shows the
 * sign-in / unauthorised screens. Exposes the user via AuthContext.
 */
export default function AuthGate({ children }) {
  const [user, setUser] = useState(null);
  const [unauthorised, setUnauthorised] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => {
      if (u && ALLOWED_EMAILS.includes(u.email)) {
        setUser(u);
        setUnauthorised(false);
      } else if (u) {
        // Signed in, but not an approved admin account
        setUser(null);
        setUnauthorised(true);
      } else {
        setUser(null);
        setUnauthorised(false);
      }
      setLoading(false);
    });
    return unsub;
  }, []);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (unauthorised) {
    return (
      <Box sx={{ p: 4 }}>
        <Typography variant="h6" gutterBottom>
          Access restricted to approved admin accounts (sales@ or john@).
        </Typography>
        <Typography variant="body1" sx={{ mb: 2 }}>
          You are currently signed in as: {auth.currentUser?.email}
        </Typography>
        <Button
          variant="outlined"
          onClick={() => {
            signOut()
              .then(() => window.location.reload())
              .catch((err) => console.error('Sign-out failed', err));
          }}
        >
          Sign out
        </Button>
      </Box>
    );
  }

  if (!user) {
    return (
      <Box sx={{ p: 4 }}>
        <Typography variant="h6" gutterBottom>
          Admin Sign-in
        </Typography>
        <Button variant="contained" onClick={signIn}>
          Sign in with Google
        </Button>
      </Box>
    );
  }

  return <AuthContext.Provider value={{ user }}>{children}</AuthContext.Provider>;
}
